import type { ElementType, HTMLAttributes, ReactNode, Ref } from 'react';

import { cx } from './cx.js';

/**
 * PLATFORM BOUNDARY.
 *
 * Box/Text/Pressable/Stack are the only files in @aj/ui that know they are
 * running on the web. Everything in components/ is composed from these four,
 * so porting to React Native means reimplementing this directory (Box → View,
 * Text → Text, Pressable → Pressable) and leaving every component untouched.
 *
 * Class strings survive the port too: NativeWind consumes the same Tailwind
 * utilities, and the utilities themselves resolve to semantic tokens only.
 */
export type BoxProps = HTMLAttributes<HTMLElement> & {
  as?: 'div' | 'section' | 'header' | 'footer' | 'nav' | 'main' | 'ul' | 'li' | 'article';
  /** Escape hatch for the few things that need the node itself — measuring a
   *  list mid-drag, moving focus. In React 19 this is an ordinary prop, so it
   *  rides along with the rest of the spread. */
  ref?: Ref<HTMLElement>;
  children?: ReactNode;
};

export function Box({ as = 'div', className, children, ...rest }: BoxProps) {
  // The `as` union widens to ElementType here on purpose: full polymorphic
  // typing costs a page of generics and buys nothing, since the allowed tags
  // are already constrained by BoxProps.
  const Tag = as as ElementType;
  return (
    <Tag className={cx(className)} {...rest}>
      {children}
    </Tag>
  );
}
